import { pipe, Runtime, Option, These } from "fp";

import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import * as Driver from "~/Driver";
import * as Link from "~/Link";
import { Order } from "~/Order";
import { Search } from "~/Search";
import * as Typography from "~/Typography";

// TODO: Replace with `Human.Name`
export type Name = {
  readonly first?: string;
  readonly last?: string;
  readonly display?: string;
};

type Props = Pick<Partial<Driver.Driver>, "id" | "name" | "inactiveDate"> & {
  readonly noLink?: boolean;
  readonly includeInactive?: boolean;
  readonly notApplicable?: boolean;
};

export const toSearch: Search.From<Name> = ({ first, last, display }) =>
  Search.join(
    Search.string(first ?? ""),
    Search.string(last ?? ""),
    Search.string(display ?? "")
  );

// TODO: This sucks
export const Component: React.Fn<Props> = ({
  id,
  name,
  inactiveDate,
  noLink = false,
  includeInactive = false,
  notApplicable = false,
}) => {

  const notApplicableString = notApplicable ? "N/A" : "Unknown Driver";
  const nameComponent = pipe(
    These.fromNullables(id, name),
    Option.fold(
      () => <>{notApplicableString}</>,
      These.fold(
        (id) =>
          noLink ? (
            <>{toString(name) ?? <>{notApplicableString}</>}</>
          ) : (
            <DriverPageLink.Component id={id} name={name} />
          ),
        (name) => <>{toString(name) ?? <>{notApplicableString}</>}</>,
        (id, name) =>
          noLink ? (
            <>{toString(name) ?? <>{notApplicableString}</>}</>
          ) : (
            <DriverPageLink.Component id={id} name={name} />
          )
      )
    )
  );

  return includeInactive && inactiveDate ? (
    <>
      {nameComponent}
      <InactiveDriverIndicator.Component inactiveDate={inactiveDate} />
    </>
  ) : (
    <>{nameComponent}</>
  );
};

namespace DriverPageLink {
  export const Component = (driver: Driver.Driver) => {
    const navigate = Driver.Page.Route.use({ driver });
    return (
      <Link.Component
        onClick={(event) => {
          // eslint-disable-next-line functional/no-expression-statement
          event.stopPropagation();
          // eslint-disable-next-line functional/no-expression-statement
          navigate.go();
        }}
      >
        {Driver.toString(driver)}
      </Link.Component>
    );
  };
}

export namespace InactiveDriverIndicator {
  const Inactive = styled.span`
     {
      color: ${Color.guardsmanRed()};
    }
  `;

  export const Component = ({
    inactiveDate,
  }: Pick<Partial<Driver.Driver>, "inactiveDate">): JSX.Element => {
    return <Inactive>{inactiveDate ? " (Deactivated)" : ""}</Inactive>;
  };
}

export namespace WithLabel {
  const Container = styled.div`
    & + & {
      margin-top: 8px;
    }
  `;

  const Label = styled(Typography.Component)`
    display: block;
    font-size: 0.85em;
    opacity: 0.7;
    margin-top: 2px;
  `;

  type Props = Pick<Partial<Driver.Driver>, "id" | "name" | "inactiveDate"> & {
    readonly label: string;
    readonly noLink?: boolean;
    readonly includeInactive?: boolean;
    readonly notApplicable?: boolean;
  };

  export const Component: React.Fn<Props> = ({
    id,
    name,
    inactiveDate,
    label,
    noLink = false,
    includeInactive = false,
    notApplicable = true,
  }) => (
    <Container>
      <Driver.Name.Component
        id={id}
        name={name}
        inactiveDate={inactiveDate}
        noLink={noLink}
        includeInactive={includeInactive}
        notApplicable={notApplicable}
      />
      <Label>{label}</Label>
    </Container>
  );
}

export const { equals, compare } = Order.fromCompare<Name>((a, b) =>
  Order.optionally(Order.string().compare)(
    `${a.last ?? ""} ${a.first ?? ""} ${a.display ?? ""}`.toLocaleLowerCase(),
    `${b.last ?? ""} ${b.first ?? ""} ${b.display ?? ""}`.toLocaleLowerCase()
  )
);

export const toString = (name?: Name): string | undefined =>
  name?.first && name?.last
    ? `${name.first} ${name.last}`
    : name?.first ?? name?.last ?? name?.display;

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      first_name: Runtime.nullable(Runtime.string()),
      last_name: Runtime.nullable(Runtime.string()),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Name | undefined =>
    !response.first_name && !response.last_name
      ? undefined
      : {
          first: response.first_name ?? undefined,
          last: response.last_name ?? undefined,
        };
}
